import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext'; // your auth
import toast from 'react-hot-toast';
import { API_URL, SERVER_URL } from '../../config/env';

interface SiteSettings {
  ministryName: string;
  email: string;
  phone: string;
  address: string;
  description: string;
  description_en: string;
  mapUrl: string;
  logoUrl?: string | null;
}

const SiteSettings: React.FC = () => {
  const { token } = useAuth(); // assume your auth context exposes token
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SiteSettings>({
    ministryName: '',
    email: '',
    phone: '',
    address: '',
    description: '',
    description_en: '',
    mapUrl: '',
    logoUrl: null
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/settings`);
        const data = await res.json();
        setSettings({
          ministryName: data.ministryName || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          description: data.description || '',
          description_en: data.description_en || '',
          mapUrl: data.mapUrl || '',

          logoUrl: data.logoUrl || null
        });
        setPreview(data.logoUrl ? `${SERVER_URL}${data.logoUrl}` : null);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load settings');
      } finally { setLoading(false); }
    };
    load();
  }, []);

  useEffect(() => {
    if (!logoFile) return;
    const url = URL.createObjectURL(logoFile);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setLogoFile(e.target.files[0]);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const form = new FormData();
      form.append('ministryName', settings.ministryName);
      form.append('email', settings.email);
      form.append('phone', settings.phone);
      form.append('address', settings.address);
      form.append('description', settings.description);
      form.append('description_en', settings.description_en);
      form.append('mapUrl', settings.mapUrl);
      if (logoFile) form.append('logo', logoFile, logoFile.name);
      

      const res = await fetch(`${API_URL}/settings`, {
        method: 'PUT',
        body: form,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Update failed' }));
        throw new Error(err.message || 'Update failed');
      }
      const updated = await res.json();
      setSettings(prev => ({ ...prev, ...updated }));
      toast.success('Settings updated');
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Loading…</div>;

  return (
    <div className="p-6 max-w-3xl">
      <h1 className="text-2xl font-bold mb-4">Site Settings</h1>

      <form onSubmit={handleSave} className="space-y-4 bg-white p-6 rounded shadow">
        <div>
          <label className="label">Minister Name</label>
          <input className="input" value={settings.ministryName} onChange={e => setSettings({...settings, ministryName: e.target.value})} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Email</label>
            <input className="input" type="email" value={settings.email} onChange={e => setSettings({...settings, email: e.target.value})} />
          </div>
          <div>
            <label className="label">Phone</label>
            <input className="input" value={settings.phone} onChange={e => setSettings({...settings, phone: e.target.value})} />
          </div>
        </div>

        <div>
          <label className="label">Address</label>
          <input className="input" value={settings.address} onChange={e => setSettings({...settings, address: e.target.value})} />
        </div>

        <div>
          <label className="label">Description in Nepali</label>
          <textarea className="input" rows={4} value={settings.description} onChange={e => setSettings({...settings, description: e.target.value})} />
        </div>

        <div>
          <label className="label">Description in English</label>
          <textarea className="input" rows={4} value={settings.description_en} onChange={e => setSettings({...settings, description_en: e.target.value})} />
        </div>

        <div>
          <label className="label">Google Map URL</label>
          <input
            className="input"
            value={settings.mapUrl}
            onChange={e => setSettings({ ...settings, mapUrl: e.target.value })}
            placeholder="Paste Google Maps embed URL here src link only not whose iframe link"
          />
        </div>

        <div>
          <label className="label">Logo (optional)</label>
          <input type="file" accept="image/*" onChange={onFileChange} />
          {preview && <img src={preview} alt="logo preview" className="mt-2 h-20 object-contain" />}
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => {
            // reload from server (undo local edits)
            setLoading(true); setLogoFile(null); setPreview(settings.logoUrl ? `${SERVER_URL}${settings.logoUrl}` : null);
            setLoading(false);
          }} className="btn-outline">Reset</button>

          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SiteSettings;

//मुख्यमन्त्री तथा मन्त्रीपरिषद्को कार्यालयमा पारदर्शिता, जवाफदेहिता र कुशल गुनासो समाधान सुनिश्चित गर्न। यसले जनताको समस्या छिटो र सजिलैँ पुग्ने बनाउँछ, जसले सरकारी सेवा सुधार्न सहयोग पुर्‍याउँछ र विश्वास बढाउँछ। सरल, छिटो र प्रभावकारी सेवा लागि "Hello CM" अत्यावश्यक छ।
//To ensure transparency, accountability, and effective grievance handling in the Office of the Chief Minister and Council of Ministers. This makes it quicker and easier for the public to raise their concerns, which helps improve government services and build trust. For simple, fast, and effective service delivery, ‘Hello CM’ is essential.
